
import React from 'react';
import { BlogPost } from './types';
import { Link, useNavigate } from 'react-router-dom';

type BlogRelatedPostsProps = {
  currentPostId: string;
  posts: BlogPost[];
  category?: string;
};

const BlogRelatedPosts: React.FC<BlogRelatedPostsProps> = ({ 
  currentPostId, 
  posts = [], 
  category 
}) => {
  const navigate = useNavigate();
  
  // Ensure posts is an array before filtering
  const safePostsArray = Array.isArray(posts) ? posts : [];
  
  if (!safePostsArray || safePostsArray.length === 0) {
    return null;
  }
  
  // Filter posts by current category (if provided) and exclude current post
  const relatedPosts = safePostsArray
    .filter(post => post.id !== currentPostId)
    .filter(post => category ? post.category === category : true)
    .slice(0, 3);

  if (relatedPosts.length === 0) return null;

  return (
    <section className="mt-16 pt-8 border-t border-gray-200 animate-fadeIn" aria-labelledby="related-posts-title">
      <h2 id="related-posts-title" className="text-2xl font-semibold mb-6 text-gray-800">Related Stories</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {relatedPosts.map((post) => (
          <article key={post.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow h-full flex flex-col">
            <Link 
              to={`/blog?post=${post.id}`}
              onClick={() => window.scrollTo(0, 0)}
              className="block h-48 w-full overflow-hidden"
            >
              <img
                src={post.image || '/placeholder.svg'}
                alt={post.title}
                className="h-full w-full object-cover"
                width="400"
                height="300"
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/placeholder.svg';
                  target.onerror = null;
                }}
              />
            </Link>
            <div className="p-4 flex flex-col flex-grow">
              <span className="text-xs font-medium text-primary">{post.category}</span>
              <h3 className="font-semibold text-lg mt-1 mb-2 line-clamp-2 text-gray-800">
                <Link 
                  to={`/blog?post=${post.id}`}
                  onClick={() => window.scrollTo(0, 0)}
                  className="hover:text-primary transition-colors"
                >
                  {post.title}
                </Link>
              </h3>
              <p className="text-sm text-gray-600 mb-4 line-clamp-2">{post.excerpt}</p>
              <Link
                to={`/blog?post=${post.id}`}
                onClick={() => window.scrollTo(0, 0)}
                className="text-primary font-medium text-sm inline-flex items-center mt-auto hover:underline"
                aria-label={`Read more about ${post.title}`}
              >
                Read More
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </Link>
            </div>
          </article>
        ))}
      </div>
      <div className="mt-8 text-center">
        <Link 
          to="/blog"
          onClick={() => window.scrollTo(0, 0)}
          className="inline-block bg-transparent border border-primary text-primary hover:bg-primary/5 px-6 py-3 rounded-md font-medium transition-all duration-300"
          aria-label="Browse all blog posts"
        >
          Browse All Stories
        </Link>
      </div>
    </section>
  );
};

export default BlogRelatedPosts;
